import { useAppSelector } from '@/store/hooks'
import { selectJobByCourse, selectJobLost } from './jobsSlice'
import { useCourseJob } from './useCourseJob'

/**
 * Сколько времени после СТАРТА прогона его резерв ещё может возвращаться.
 * Сумма двух контрактных пределов с запасом: сама джоба живёт до 10 минут
 * (клиентского таймаута нет, она честно стоит в `running`), и ещё до 10 минут
 * после её смерти работает жнец, возвращающий резерв. Тридцать минут — это
 * 10 + 10 плюс запас на расхождение часов и медленный жнец.
 *
 * Ограничение по возрасту обязательно, а не «на всякий случай»: `aiJobs` лежит
 * в persist-whitelist, `byCourse` переживает перезагрузку и чистится только
 * действием автора (закрытие итога — `detach`). Без окна незакрытый неделю
 * назад итог навсегда прятал бы кнопку «Пополнить» у этого курса, и баг
 * «кнопка иногда врёт» превратился бы в баг «кнопки нет никогда».
 */
export const RESERVE_RETURN_WINDOW_MS = 30 * 60 * 1000

/**
 * «Кредиты предыдущего прогона ещё возвращаются» (§1.4).
 *
 * Гейт «курс занят» открывается РАНЬШЕ, чем жнец возвращает резерв: курс
 * освобождается по TTL токена, а деньги приходят назад до десяти минут спустя.
 * В этом окне повторный старт отбивается `400 not_enough_ai_credits`, хотя
 * пополнять нечего — кнопка оплаты там была бы прямым обманом.
 *
 * Признаком служит СВЕЖАЯ незакрытая запись реестра этого курса (или отметка о
 * пропавшем прогоне): оба случая означают «прошлый прогон ещё на экране, автор
 * его не закрыл». Своего таймштампа финала не заводим — `finished_at` у
 * `failed`-джоб приходит `null`, — поэтому возраст считаем от `startedAt`
 * записи, см. `RESERVE_RETURN_WINDOW_MS`. Отметку `lostByCourse` возрастом не
 * ограничиваем: она эфемерна и сносится `REHYDRATE` (см. `jobsSlice`).
 *
 * УСПЕШНЫЙ прогон из признака исключён. Запись реестра не помнит терминального
 * статуса, поэтому «незакрытый итог» одинаково выглядит и как упавший прогон
 * (резерв вернётся), и как успешный (кредиты списаны честно, возвращать
 * нечего). У второго отказ по деньгам НАСТОЯЩИЙ — успешный прогон мог потратить
 * остаток, — и текст «кредиты ещё возвращаются» врал бы, пряча кнопку оплаты на
 * полчаса ради бесполезного повтора. Статус берём у `useCourseJob`: он читает
 * ту же джобу из общего кеша RTK Query, второго запроса это не добавляет.
 */
export const useReserveReturning = (courseId: number | null): boolean => {
  const { job } = useCourseJob(courseId ?? 0)
  const hasOpenRun = useAppSelector((state) => {
    if (courseId === null) return false
    const entry = selectJobByCourse(state, courseId)
    const isFresh = entry !== undefined && Date.now() - entry.startedAt < RESERVE_RETURN_WINDOW_MS
    return isFresh || selectJobLost(state, courseId)
  })
  return job?.status === 'success' ? false : hasOpenRun
}
